import Vue from "vue";
import App from "./App.vue";
// 导入vant
import Vant, { Locale } from "vant";
// 引入组件库
import VueEasytable from "vue-easytable";
// 业务接口域名状态中心 —— ping 出最快域名后回写到 httpUrl
import { setHttpUrl, markHttpUrlReady } from "./request/domain";

// 导入 Vant 语言包
import zhCN from "vant/es/locale/lang/zh-CN";
import enUS from "vant/es/locale/lang/en-US";
import viVN from "vant/es/locale/lang/vi-VN";
// 注意：Vant 暂不支持马来语，将使用英文包替代

// import zhCN from "./i18n/config/zh.js";
// import enUS from "./i18n/config/en.js";
// 导入路由
import router from "./router/index";
// 导入vuex
import store from "./store/index";
// 移动端适配
import "lib-flexible";
// import 'amfe-flexible'
// import JSBridge from "./utils/JSBridge.js";
// Vue.prototype.bridge = JSBridge;
import moment from "moment";
import _ from "lodash";
// 全局样式
import "vant/lib/index.css";
import "vue-easytable/libs/theme-default/index.css";
import "./assets/style/variable.scss";
import "./assets/iconfont/iconfont.css";
import "./assets/style/index.scss";
// import VConsole from 'vconsole'
// i18n多语言
import i18n from "./i18n";

// 项目启动立即调用 ip 接口，获取访问者 IP/地理位置信息
// 说明：原本想用 ipinfo.io，但其浏览器端 CORS 支持不稳定（且不带 Accept 头会返回 406），
// 改用 ipapi.co —— 默认带 Access-Control-Allow-Origin: *，字段覆盖 ip/country/region/city 等。
//
// 整体逻辑：
//   1. 拿到访客 country。非 CN 直接放行业务请求；
//   2. CN 才请求 domain/list 拿到候选 api 域名；
//   3. 并发 ping，挑响应最快的写入 httpUrl；
//   4. 任何分支结束/失败都必须调用 markHttpUrlReady()，否则业务请求会被永久卡住。
//
// 仅在真正的生产构建（npm run build）下跑 ipapi → domain/list → ping 切换。
// 注意：build.test 也设了 NODE_ENV=production（为了产出生产级构建产物，避免
// development 构建上线白屏），所以不能只看 NODE_ENV —— 必须同时排除
// .env.test 注入的 VUE_APP_BUILD_MODE === "test"，让测试包仍按 .env.test 的
// 默认域名直接放行。开发（npm run serve）走 NODE_ENV !== "production" 分支放行。
if (
  process.env.NODE_ENV === "production" &&
  process.env.VUE_APP_BUILD_MODE !== "test"
) {
  // 另外加一个 8s 兜底超时，防止 ipapi/domain-list/ping 整链路全部挂掉时
  // 页面上的业务请求一直 pending。
  const READY_FALLBACK_TIMEOUT = 8000;
  const readyFallbackTimer = setTimeout(() => {
    console.warn(
      `[domain] startup flow exceeded ${READY_FALLBACK_TIMEOUT}ms, force ready`
    );
    markHttpUrlReady();
  }, READY_FALLBACK_TIMEOUT);
  const finishReady = () => {
    clearTimeout(readyFallbackTimer);
    markHttpUrlReady();
  };

  fetch("https://ipapi.co/json", {
    method: "GET",
    credentials: "omit",
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`ipapi HTTP ${res.status}`);
      }
      return res.json();
    })
    .then(async (data) => {
      console.log("[ipinfo]", data);
      try {
        sessionStorage.setItem("ipinfo", JSON.stringify(data));
      } catch (e) {
        // ignore storage errors
      }
      // 如果是中国大陆访问，继续请求备用域名列表
      // ipapi 的国家字段名为 country / country_code（两个都是 ISO 二字码，如 "CN"）
      const country = data && (data.country || data.country_code);
      if (country !== "CN") {
        // 非 CN，无需切换域名，直接放行
        return;
      }
      const res = await fetch(
        "https://api.sejahteratech.com/api/app/domain/list",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          // body: JSON.stringify({}),
        }
      );
      if (!res.ok) {
        throw new Error(`domain/list HTTP ${res.status}`);
      }
      const domainData = await res.json();
      console.log("[domain/list]", domainData);
      try {
        sessionStorage.setItem("domainList", JSON.stringify(domainData));
      } catch (e) {
        // ignore storage errors
      }
      const items = extractDomainItems(domainData);
      if (!items.length) {
        console.warn("[domain/list] no domain extracted, skip ping");
        return;
      }
      // 并发 ping，全部 settle 后选出最快域名并写入 httpUrl
      await pingDomainsAndSwitch(items);
    })
    .catch((err) => {
      console.warn("[ipinfo/domain] request failed:", err);
    })
    .finally(() => {
      // 不管走哪个分支、是否切换成功，都解锁业务请求闸门
      finishReady();
    });
} else {
  // 开发 / 测试构建：直接放行，使用对应 .env 文件里的默认域名
  markHttpUrlReady();
}

// 从 domain/list 接口的响应里抽出每一项（含 api / socket / h5 / resources）
// 真实结构：{ code: 10000, data: { version, list: [{ api, socket, h5, resources }, ...] }, msg }
// 返回标准化后的 list 项数组：[{ api, socket, h5, resources }]
// api / resources 等字段会自动补 https:// 前缀并去掉末尾斜杠，便于拼接
function extractDomainItems(payload) {
  if (!payload) return [];
  const list =
    (payload.data && payload.data.list) ||
    payload.list ||
    (Array.isArray(payload) ? payload : []);
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const api = normalizeUrl(item.api);
      if (!api) return null; // 没有 api 域名的项直接丢弃
      return {
        api,
        socket: item.socket || "",
        h5: normalizeUrl(item.h5),
        resources: normalizeUrl(item.resources),
      };
    })
    .filter(Boolean);
}

function normalizeUrl(u) {
  if (typeof u !== "string" || !u) return "";
  const withProto = /^https?:\/\//i.test(u) ? u : `https://${u}`;
  return withProto.replace(/\/+$/, "");
}

// 并发 ping 所有域名，全部完成后挑出响应最快的可用域名，调用 setHttpUrl 切换。
// 同时把"被 ping 选中的整条 list 项"存入 sessionStorage.activeDomainItem，
// 方便业务页（如 H5 落地页）按选中域名取对应的 resources / h5 等字段。
// 注意：解锁业务请求闸门由外层 .finally 统一负责，这里只管"切换"。
async function pingDomainsAndSwitch(items) {
  const PING_TIMEOUT = 5000;
  const tasks = items.map(async (item) => {
    const start = Date.now();
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), PING_TIMEOUT);
    try {
      const res = await fetch(`${item.api}/api/app/url/ping`, {
        method: "GET",
        credentials: "omit",
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`ping ${item.api} HTTP ${res.status}`);
      return { item, cost: Date.now() - start, ok: true };
    } catch (err) {
      return { item, ok: false, error: String(err && err.message) };
    } finally {
      clearTimeout(timer);
    }
  });

  const results = await Promise.all(tasks);
  console.log("[ping] results:", results);
  const alive = results.filter((r) => r.ok).sort((a, b) => a.cost - b.cost);
  if (!alive.length) {
    console.warn("[ping] no domain alive, keep default httpUrl");
    return;
  }
  const pickedItem = alive[0].item;
  try {
    // 把"选中"的整条 list 项存起来 —— 业务页通过 activeDomainItem.resources 拼下载地址
    sessionStorage.setItem("activeDomainItem", JSON.stringify(pickedItem));
  } catch (e) {
    // ignore
  }
  // 切换 api.js 里的 httpUrl —— 注意：在此之前已发出的请求仍走旧地址
  setHttpUrl(pickedItem.api);
  console.log(
    `[ping] switched httpUrl -> ${pickedItem.api} (cost ${alive[0].cost}ms)`
  );
}

Vue.config.productionTip = false;

Vue.prototype.$moment = moment;
Vue.prototype.$lodash = _;

Vue.use(Vant);

// 根据当前语言设置 Vant 的 Locale
const setVantLocale = (lang) => {
  switch (lang) {
    case 'en':
      Locale.use('en-US', enUS);
      break;
    case 'ms':
      Locale.use('en-US', enUS); // 马来语暂时使用英文包
      break;
    case 'vi':
      Locale.use('vi-VN', viVN);
      break;
    case 'zh-TW':
      Locale.use('zh-CN', zhCN); // 繁体中文暂时使用简体中文包
      break;
    case 'in':
    case 'id':
      Locale.use('en-US', enUS); // 印尼语暂时使用英文包
      break;
    case 'th':
      Locale.use('en-US', enUS); // 泰国语暂时使用英文包（Vant 无泰语包）
      break;
    default:
      Locale.use('zh-CN', zhCN);
      break;
  }
};

// 初始化语言设置
setVantLocale(i18n.locale);

// 监听语言变化
i18n.vm.$watch('locale', (newLocale) => {
  setVantLocale(newLocale);
});

Vue.use(VueEasytable);

// new VConsole();

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount("#app");
